import type {
  DaperStory,
  PersonalizationTopic,
  UserEngagement,
} from "@foundation/intercom-client";
import Logger from "@foundation/logger";
import { ALLOWED_TOPICS, MAX_TOPICS } from "./constants";
import type { Topic, TopicsWithStories, AutofillData } from "./types";
import intercom from "~config/intercom";
import { logError } from "~utils/newrelic/newrelicBrowser";

const MINIMUM_TOPICS_TO_SHOW = 8;
const MAXIMUM_TOPICS_TO_SHOW = 20;

export const parseStoriesToExcludeFromAutofillData = (
  autofillData?: AutofillData
) => {
  const idsToExclude =
    typeof autofillData?.args === "object" &&
    typeof autofillData?.args?.exclude === "string"
      ? autofillData?.args?.exclude
      : "";

  return idsToExclude
    .split(";")
    .map((storyToExclude) => storyToExclude.split("|")[1])
    .filter(Boolean)
    .join(",");
};

export const getOptimisticTopicFollow = (topicId: string): Topic => ({
  id: topicId,
  name: "",
  isFollowedByUser: true,
});

export const addFollowInPlace = (
  currentTopics: Topic[],
  followedTopic: Topic
) =>
  currentTopics.map((currentTopic) => {
    if (currentTopic.id === followedTopic.id) {
      return followedTopic;
    }

    return currentTopic;
  });

export const addFollowInFront = (
  currentTopics: Topic[],
  followedTopic: Topic
) =>
  [
    followedTopic,
    ...currentTopics.filter(
      (currentTopic) => currentTopic.id !== followedTopic.id
    ),
  ].splice(0, MAX_TOPICS);

export const getTopicIdMap = (topics: Topic[]) =>
  topics.reduce(
    (acc, topic) => ({ ...acc, [topic.id]: true }),
    {} as {
      [key: string]: boolean;
    }
  );

export const getFollowedTopics = (topics: Topic[]) =>
  topics.filter((topic) => topic.isFollowedByUser);

export const getFollowedTopicIdsMap = (topics: Topic[]) =>
  getTopicIdMap(getFollowedTopics(topics));

export const dedupeStoriesFromTopicLists = (
  topicsWithStories: TopicsWithStories
) => {
  const storiesWithTopic = Object.entries(topicsWithStories).reduce(
    (acc, [topicId, stories]) => {
      stories.forEach((story) => {
        const { contentTags, id: storyId } = story;

        if (!contentTags) {
          return;
        }

        const storyScore = contentTags.find(
          (tag) => tag.id === topicId
        )?.derivedScore;

        const existingRecord = acc[storyId];

        if (
          (!existingRecord && storyScore) ||
          (existingRecord &&
            storyScore &&
            existingRecord.leadingScore < storyScore)
        ) {
          // eslint-disable-next-line no-param-reassign
          acc[story.id] = {
            leadingTopic: topicId,
            leadingScore: storyScore,
          };
        }

        return;
      });

      return acc;
    },
    {} as {
      [x: string]: {
        leadingTopic: string;
        leadingScore: number;
      };
    }
  );

  return Object.keys(topicsWithStories).reduce((acc, topicId) => {
    // eslint-disable-next-line no-param-reassign
    acc[topicId] = topicsWithStories[topicId].filter(
      (story) => storiesWithTopic[story.id]?.leadingTopic === topicId
    );

    return acc;
  }, {} as TopicsWithStories);
};

export const getImageID = (story: DaperStory) => {
  return (
    story.extraMedia?.thumbnail?.default.id ||
    Object.values(story.attachments?.image || {}).find((image) =>
      image.placement?.includes("thumbnail")
    )?.id
  );
};

export const transformPersonalizationTopicsToTopics = (
  personalizationTopics: PersonalizationTopic[]
): Topic[] =>
  personalizationTopics.map(({ name, assetID }) => ({
    id: assetID,
    name,
    isFollowedByUser: false,
  }));

export const filterAllowedTopics = (topics: Topic[]) =>
  topics.filter((topic) => ALLOWED_TOPICS[topic.id]);

export const transformUserEngagementsToTopics = (
  userEngagements: UserEngagement[]
): Topic[] => {
  return filterAllowedTopics(
    userEngagements.map(({ resource }) => ({
      name: resource.name,
      id: resource.id,
      isFollowedByUser: true,
    }))
  );
};

export const getRecommendedTopics = ({ limit }: { limit: number }) =>
  intercom.getRecommendedTPR({
    resourceTypes: "topic",
    limit,
  });

export const getUserTopics = () => intercom.getUserTopics();

export const getBackfilledTopics = async (numberToBackfill: number) => {
  if (!numberToBackfill) return [];

  const recommendedResources = await getRecommendedTopics({ limit: 50 });
  const backfilledTopics = transformPersonalizationTopicsToTopics(
    recommendedResources as PersonalizationTopic[]
  );
  const allowedBackfilledTopics = filterAllowedTopics(backfilledTopics);

  const fallbackTopics: Topic[] = Object.keys(ALLOWED_TOPICS).map((id) => ({
    id,
    name: ALLOWED_TOPICS[id].name,
    isFollowedByUser: false,
  }));

  const dedupedTopics = Array.from(
    [...allowedBackfilledTopics, ...fallbackTopics]
      .reduce(
        (map, topic) => (map.has(topic.id) ? map : map.set(topic.id, topic)),
        new Map()
      )
      .values()
  );

  return dedupedTopics.slice(0, numberToBackfill);
};

/* eslint-disable @typescript-eslint/no-explicit-any */
type DedupeProps<T> = {
  refrenceList: T[];
  listToDedupe: T[];
  key: string;
};

export const dedupeLists = <T extends Record<string, any>>({
  refrenceList,
  listToDedupe,
  key = "id",
}: DedupeProps<T>): T[] => {
  const referenceKeys = new Set(
    refrenceList.map((refrenceListItem) => refrenceListItem[key])
  );

  return listToDedupe.filter(
    (listToDedupeTopic) => !referenceKeys.has(listToDedupeTopic[key])
  );
};

export const getTopics = async ({ bconnectId }: { bconnectId?: string }) => {
  let topics: Topic[] = [];

  if (bconnectId) {
    try {
      topics = await getUserTopics().then(transformUserEngagementsToTopics);
    } catch (e) {
      const err = new Error(`[getTopics] - failed to fetch user topics: ${e}`);

      logError(err, {
        bconnectId,
      });

      Logger.error(err);
    }
  }

  let backfilledTopics: Topic[] = [];

  try {
    backfilledTopics = await getBackfilledTopics(
      Math.max(0, MINIMUM_TOPICS_TO_SHOW - topics.length)
    );
  } catch (e) {
    const err = new Error(
      `[getTopics] - failed to fetch backfilled topics: ${e}`
    );

    logError(err);

    Logger.error(err);
  }

  const dedupedBackfilledTopics = dedupeLists({
    refrenceList: topics,
    listToDedupe: backfilledTopics,
    key: "id",
  });

  return topics
    .concat(dedupedBackfilledTopics)
    .slice(0, MAXIMUM_TOPICS_TO_SHOW);
};
